import { useState, useEffect } from 'react';
import { 
  Link2, 
  Search, 
  Loader2, 
  CheckCircle, 
  XCircle, 
  Clock, 
  ExternalLink,
  AlertCircle 
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import { vendorApi, platformsApi } from '@/services/api';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';

const STATUS_CONFIG = {
  pending: { icon: Clock, color: 'text-amber-500', bgColor: 'bg-amber-100', label: 'Pending Review' },
  approved: { icon: CheckCircle, color: 'text-green-600', bgColor: 'bg-green-100', label: 'Approved' },
  rejected: { icon: XCircle, color: 'text-red-500', bgColor: 'bg-red-100', label: 'Rejected' },
};

export function PlatformLinkRequestForm({ onSuccess, linkedPlatformIds = [] }) {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [platforms, setPlatforms] = useState([]);
  const [searchOpen, setSearchOpen] = useState(false);
  
  // Form state
  const [selectedPlatformId, setSelectedPlatformId] = useState('');
  const [isNotListed, setIsNotListed] = useState(false);
  const [platformName, setPlatformName] = useState('');
  const [proofUrl, setProofUrl] = useState('');
  const [note, setNote] = useState('');
  
  const { toast } = useToast();

  // Load platforms for selection
  useEffect(() => {
    const loadPlatforms = async () => {
      try {
        const data = await platformsApi.getAll();
        // Filter out already linked platforms
        const available = data.filter(p => !linkedPlatformIds.includes(p.id));
        setPlatforms(available);
      } catch (err) {
        console.error('Failed to load platforms:', err);
      }
    };
    
    if (isOpen) {
      loadPlatforms();
    }
  }, [isOpen, linkedPlatformIds]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!proofUrl.trim()) {
      toast({ title: 'Error', description: 'Proof URL is required', variant: 'destructive' });
      return;
    }
    
    if (!isNotListed && !selectedPlatformId) {
      toast({ title: 'Error', description: 'Please select a platform or check "My platform isn\'t listed"', variant: 'destructive' });
      return;
    }
    
    if (isNotListed && !platformName.trim()) {
      toast({ title: 'Error', description: 'Platform name is required', variant: 'destructive' });
      return;
    }
    
    setIsLoading(true);
    try {
      const requestData = {
        requested_platform_id: isNotListed ? null : selectedPlatformId,
        requested_platform_name: isNotListed ? platformName : null,
        proof_url: proofUrl,
        note: note || null,
      };
      
      await vendorApi.createLinkRequest(requestData);
      
      toast({ title: 'Success', description: 'Request submitted for review' });
      setIsOpen(false);
      resetForm();
      onSuccess?.();
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setSelectedPlatformId('');
    setIsNotListed(false);
    setPlatformName('');
    setProofUrl('');
    setNote('');
  };

  const selectedPlatform = platforms.find(p => p.id === selectedPlatformId);

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="gap-2" data-testid="request-platform-link-btn">
          <Link2 className="w-4 h-4" />
          Request Platform Link
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Request Platform Link</DialogTitle>
          <DialogDescription>
            Submit a request to link another platform to your account. 
            All requests are reviewed to prevent misrepresentation.
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Platform Selection */}
          {!isNotListed && (
            <div className="space-y-2">
              <Label>Select Platform</Label>
              <Popover open={searchOpen} onOpenChange={setSearchOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    className="w-full justify-between"
                    data-testid="platform-search-trigger"
                  >
                    {selectedPlatform ? selectedPlatform.name : "Search platforms..."}
                    <Search className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[400px] p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Search platforms..." />
                    <CommandList>
                      <CommandEmpty>No platform found.</CommandEmpty>
                      <CommandGroup>
                        {platforms.map((platform) => (
                          <CommandItem
                            key={platform.id}
                            value={platform.name}
                            onSelect={() => {
                              setSelectedPlatformId(platform.id);
                              setSearchOpen(false);
                            }}
                          >
                            {platform.name}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
          )}

          {/* Not Listed Checkbox */}
          <div className="flex items-center space-x-2">
            <Checkbox 
              id="not-listed" 
              checked={isNotListed}
              onCheckedChange={(checked) => {
                setIsNotListed(checked);
                if (checked) setSelectedPlatformId('');
              }}
              data-testid="not-listed-checkbox"
            />
            <Label htmlFor="not-listed" className="text-sm cursor-pointer">
              My platform isn't listed
            </Label>
          </div>

          {/* Platform Name (if not listed) */}
          {isNotListed && (
            <div className="space-y-2">
              <Label htmlFor="platform-name">Platform Name *</Label>
              <Input
                id="platform-name"
                value={platformName}
                onChange={(e) => setPlatformName(e.target.value)}
                placeholder="Enter your platform name"
                data-testid="platform-name-input"
              />
            </div>
          )}

          {/* Proof URL */}
          <div className="space-y-2">
            <Label htmlFor="proof-url">Proof URL *</Label>
            <Input
              id="proof-url"
              type="url"
              value={proofUrl}
              onChange={(e) => setProofUrl(e.target.value)}
              placeholder="https://your-platform.com/about or documentation link"
              data-testid="proof-url-input"
            />
            <p className="text-xs text-muted-foreground">
              Link to official website, documentation, or public evidence of your association
            </p>
          </div>

          {/* Note */}
          <div className="space-y-2">
            <Label htmlFor="note">Additional Notes (optional)</Label>
            <Textarea
              id="note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Any additional information to support your request"
              rows={2}
              data-testid="note-input"
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setIsOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading} data-testid="submit-request-btn">
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                'Submit Request'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// Component to display request history
export function PlatformLinkRequestHistory() {
  const [requests, setRequests] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadRequests = async () => {
      try {
        const data = await vendorApi.getLinkRequests();
        setRequests(data);
      } catch (err) {
        console.error('Failed to load requests:', err);
      } finally {
        setIsLoading(false);
      }
    };
    loadRequests();
  }, []);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (requests.length === 0) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Platform Link Requests</CardTitle>
        <CardDescription>Your submitted platform link requests</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {requests.map((request) => {
            const statusConfig = STATUS_CONFIG[request.status] || STATUS_CONFIG.pending;
            const StatusIcon = statusConfig.icon;
            
            return (
              <div 
                key={request.id}
                className="flex items-center justify-between p-3 border rounded-lg"
              >
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{request.platform_name}</span>
                    <Badge 
                      variant="outline" 
                      className={cn('text-xs', statusConfig.color)}
                    >
                      <StatusIcon className="w-3 h-3 mr-1" />
                      {statusConfig.label}
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Submitted: {new Date(request.created_at).toLocaleDateString()}
                  </p>
                  {request.status === 'rejected' && request.admin_notes && (
                    <div className="mt-2 p-2 bg-red-50 rounded text-sm text-red-700">
                      <strong>Reason:</strong> {request.admin_notes}
                    </div>
                  )}
                </div>
                <a 
                  href={request.proof_url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-muted-foreground hover:text-foreground"
                >
                  <ExternalLink className="w-4 h-4" />
                </a>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
